import { useMutation, useQuery } from '@apollo/client';
import styled from "@emotion/styled";
import { InternterReminderBox, IconCheck } from "@kompare/kompare-ui-library";
import dayjs from "dayjs";
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFunnelNavigate } from 'src/common/hooks/useFunnelNavigate.hook';
import { formatNumber, i18n, mediaQuery, persistor, setCrossSell, setCustomer, setExamDraft, setFlowType, setProgressBarPercentage, setSoId, setSoIdWeb, useAppDispatch, useAppSelector, useCreateSoWithCaptcha } from 'src/common';
import { apolloClientAdditional, createSoAdditional, CreateSoInput, CrossSellSource } from 'src/common/api/apollo/additional';
import { Button, CaptchaChallenge, CrossSellAdditionalCard, Divider, Flex, Text, Card } from 'src/components';
import { confirmWsPay, getCrossSellAdditionalInfo, setPaymentAttempt } from 'src/common/api/apollo/supplementary';
import config from 'src/config';
import { colors, footer, header } from 'src/theme';
import { AppRoutes, AppRoutesAdditionalCrossell } from '../../_router/app.routes';

const FlexStyle = styled(Flex)({
  minHeight: `calc(100vh - ${footer.mobileHeight + header.mobileHeight + 80}px)`,

  [mediaQuery('mobile')]: {
    minHeight: `calc(100vh - ${footer.height + header.mobileHeight + 80}px)`,
  },

  [mediaQuery('laptopL')]: {
    minHeight: `calc(100vh - ${footer.height + header.height + 80}px)`,
  },
});

const ContainerStyle = styled(Flex)({
  width: '100%',
  maxWidth: 480,
  margin: '0 auto',
});

const SuccessBadgeStyle = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 72,
  height: 72,
  borderRadius: '50%',
  backgroundColor: colors.success,
});

type UrlParams = {
  [key: string]: string;
};

// Definišite mapu za mapiranje starih ključeva na nove ključeve
const keyMapping: { [key: string]: string } = {
  CustomerFirstname: 'wsPayCustomerFirstName',
  CustomerSurname: 'wsPayCustomerLastName',
  ShoppingCartID: 'wsPayShoppingCartId',
  DateTime: 'wsPayDateTime',
  Amount: 'wsPayAmount',
  STAN: 'wsPaySTAN',
  Partner: 'wsPayPartner',
  WsPayOrderId: 'wsPayOrderId',
  PaymentType: 'wsPayPaymentType',
  CreditCardNumber: 'wsPayCreditCardNumber',
  PaymentPlan: 'wsPayPaymentPlan',
  Success: 'wsPaySuccess',
  ApprovalCode: 'wsPayApprovalCode',
  Signature: 'wsPayConfirmationSignature',
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Flex justify="space-between" align="flex-start" gap={16}>
    <Text type="base" color={colors.text3}>
      {label}
    </Text>
    <Text type="base" bold color={colors.text1} style={{ textAlign: 'right' }}>
      {value}
    </Text>
  </Flex>
);

const CheckoutSuccessPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useFunnelNavigate();

  const offerId = useAppSelector((store) => store.model.offer.offerId);

  const [params, setParams] = useState<UrlParams | null>(null);
  const [crossSellSubmitting, setCrossSellSubmitting] = useState(false);

  const location = useLocation();

  const { data: crossSellInfo } = useQuery(getCrossSellAdditionalInfo, {
    variables: { offerId: offerId ?? '' },
    skip: !offerId,
  });

  // Show the cross-sell block only when the backend explicitly confirms
  // the customer has no active additional policy (null/undefined → hidden).
  const showCrossSell = crossSellInfo?.hasActiveAdditionalHealthPolicy === false;
  const overflowVoucher = crossSellInfo?.overflowVoucherStatus;

  // The cross-sell creates an *additional* SO on the additional client, behind the same
  // captcha ladder as the funnel entry (the ladder counts attempts per source).
  const { submit: createCrossSellSo, captcha } = useCreateSoWithCaptcha(createSoAdditional, {
    page: 'dzo-crosssell',
    client: apolloClientAdditional,
  });

  const contractorPerson = crossSellInfo?.checkoutHealthOfferData?.contractor?.person;
  const insuredPerson = crossSellInfo?.checkoutHealthOfferData?.insuredPerson;

  const createDodatnoCrossSellSo = () => {
    if (!offerId || crossSellSubmitting) return;

    const firstName = contractorPerson?.firstName?.value ?? insuredPerson?.firstName?.value ?? '';
    const lastName = contractorPerson?.lastName?.value ?? insuredPerson?.lastName?.value ?? '';
    const email = contractorPerson?.partnerContactEmail?.value ?? insuredPerson?.partnerContactEmail?.value ?? '';
    const phoneNumber = contractorPerson?.phoneNumber?.value ?? '';
    const birthDate = contractorPerson?.birthDate?.value ?? insuredPerson?.birthDate?.value ?? '';

    const input: CreateSoInput = {
      firstName,
      lastName,
      email,
      phoneNumber,
      dateOfBirth: birthDate || undefined,
      productTypeCode: 'dodatno',
      source: 'crossell-dodatno',
      crossSell: {
        source: CrossSellSource.GeneralSo,
        sourceId: offerId,
        productTypeCode: 'dzo',
      },
    };

    setCrossSellSubmitting(true);
    createCrossSellSo(
      { input },
      {
        onSuccess: (response) => {
          const soId = response.data?.createSo;
          dispatch(setCustomer({ firstName, lastName, email, phoneNumber, birthDate }));
          dispatch(setSoId(soId));
          dispatch(setSoIdWeb(soId));
          dispatch(setExamDraft(null));
          dispatch(setFlowType('web'));
          dispatch(setCrossSell({
            source: CrossSellSource.GeneralSo,
            sourceId: offerId,
            productTypeCode: 'dzo',
            newSoSource: 'crossell-dodatno',
          }));
          navigate(`${AppRoutesAdditionalCrossell.HEALTH_EXAMS.fullPath}/${birthDate}`);
        },
        // Runs after every attempt, a challenge included — the CTA must not stay
        // spinning while the widget waits for the user.
        onSettled: () => setCrossSellSubmitting(false),
      },
    );
  };

  useEffect(() => {
    if (params) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'gtmt.purchase',
        trackingData: {
          category: 'Zdravstveno osiguranje',
          revenue: params.wsPayAmount
        },
      });
    }
  }, [location, params]);

  useEffect(() => {
    dispatch(setProgressBarPercentage(0));

    const queryParams = new URLSearchParams(window.location.search);
    const paramsObject: UrlParams = Array.from(queryParams.entries()).reduce((acc, [key, value]) => {
      if (keyMapping[key]) {
        const newKey = keyMapping[key];
        acc[newKey] = value;
      }
      return acc;
    }, {} as UrlParams);
    setParams(paramsObject);

    persistor.purge();

  }, []);

  const [setPaymentAttemptMutation] = useMutation(setPaymentAttempt);
  const [confirmWsPayMutation] = useMutation(confirmWsPay);

  useEffect(() => {
    if (offerId && params) {
      setPaymentAttemptMutation({
        variables: { id: offerId }
      });

      confirmWsPayMutation({
        variables: { id: offerId, input: params }
      });
    }
  }, [params, offerId, setPaymentAttemptMutation, confirmWsPayMutation]);

  const navigateToHome = () => {
    window.location.href = config.REACT_APP_KOMPARE_HOME!;
  };

  const navigateToReminderPage = () => {
    window.location.href = AppRoutes.CHECKOUT_REMINDER_PAGE.path.replace(':offerId', offerId ?? '');
  }

  const t = (key: string) => i18n.t(`pages.checkoutSuccessPage.${key}`);

  const insuredName = [insuredPerson?.firstName?.value, insuredPerson?.lastName?.value].filter(Boolean).join(' ');
  const insurerName = crossSellInfo?.checkoutHealthOffer?.supplementary?.brandName ?? '';
  const policyNumber = insuredPerson?.insuranceNumber?.value ?? '';
  const newPolicyStartDate = crossSellInfo?.checkoutHealthOfferData?.newPolicyStartDate;
  const coverageStart = newPolicyStartDate ? dayjs(newPolicyStartDate).format('DD.MM.YYYY.') : '';

  const cardType = params?.wsPayPaymentType;
  const cardDigits = params?.wsPayCreditCardNumber?.replace(/\D/g, '');
  const cardLast4 = cardDigits ? cardDigits.slice(-4) : undefined;
  const paymentMethodValue = cardType && cardLast4
    ? `${t('paymentMethodCard')} (${cardType} •••• ${cardLast4})`
    : t('paymentMethodCard');

  const parsedWsPayAmount = params?.wsPayAmount ? parseFloat(params.wsPayAmount.replace(',', '.')) : NaN;
  const amount = !Number.isNaN(parsedWsPayAmount)
    ? parsedWsPayAmount
    : crossSellInfo?.checkoutHealthOffer?.supplementary?.total?.creditCard ?? undefined;
  const formattedAmount = amount !== undefined ? formatNumber(amount, '€') : '';

  return (
    <FlexStyle vertical justify='center' align='center' style={{ paddingTop: 32, paddingBottom: 32 }}>
      <ContainerStyle vertical align="center" gap={24}>
        <SuccessBadgeStyle>
          <IconCheck color={colors.white} width={40} height={40} />
        </SuccessBadgeStyle>

        <Flex vertical align="center" gap={8} style={{ width: 'max-content' }}>
          <Text type="large" bold color={colors.text1}>
            {t('successTitle')}
          </Text>
          <Text type="small" color={colors.text3}>
            {t('successSubtitle')}
          </Text>
        </Flex>

        <Card padding={24} radius={16}>
          <Flex vertical gap={16}>
            <Text type="large" bold color={colors.text1}>
              {t('orderDetailsTitle')}
            </Text>
            <Divider dashed={false} color={colors.border5} />
            <Flex vertical gap={12}>
              {insuredName && <DetailRow label={t('insuredLabel')} value={insuredName} />}
              <DetailRow label={t('policyLabel')} value={t('policyValue')} />
              {insurerName && <DetailRow label={t('insurerLabel')} value={insurerName} />}
              {policyNumber && <DetailRow label={t('policyNumberLabel')} value={policyNumber} />}
              {coverageStart && <DetailRow label={t('coverageStartLabel')} value={coverageStart} />}
              <DetailRow label={t('paymentMethodLabel')} value={paymentMethodValue} />
            </Flex>
            {formattedAmount && (
              <>
                <Divider dashed={false} color={colors.border5} />
                <Flex justify="space-between" align="center" gap={16}>
                  <Text type="large" bold color={colors.text1}>
                    {t('totalLabel')}
                  </Text>
                  <Text type="xLarge" bold color={colors.primary}>
                    {formattedAmount}
                  </Text>
                </Flex>
              </>
            )}
          </Flex>
        </Card>

        <Card padding={16} radius={12} background={colors.success2}>
          <Text type="small" color={colors.text2} style={{ textAlign: 'center', display: 'block' }}>
            {t('emailInfo')}
          </Text>
        </Card>

        <Flex vertical align="center" gap={16} style={{ width: '100%' }}>
          <Button
            btnType="textPrimary"
            onClick={navigateToHome}
            text={t('homeButton')}
            style={{ textDecoration: 'underline' }}
          />
        </Flex>
      </ContainerStyle>

      {showCrossSell && (
        <CrossSellAdditionalCard
          voucherActive={overflowVoucher?.active ?? false}
          voucherDescription={overflowVoucher?.voucherDescription || undefined}
          loading={crossSellSubmitting}
          beforeCta={<CaptchaChallenge {...captcha} />}
          ctaDisabled={captcha.required}
          onCtaClick={createDodatnoCrossSellSo}
        />
      )}
      <InternterReminderBox
        onCheckPricesClick={() => window.open("https://kompare.hr/telekomunikacije/telefon-internet-tv/", "_blank")}
        onReminderClick={navigateToReminderPage}
      />
    </FlexStyle>
  );
}

export default CheckoutSuccessPage;
